import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import api from "../pages/chat/api";

// Per-org cache of the reviewer verdict, persisted so the value is known
// synchronously on mount/workspace-switch. Without this the hook starts at
// `false` and only flips to `true` after the async members fetch, which makes
// reviewer-gated controls (e.g. the Create Project button) flash visible for a
// moment before hiding. Reading the cache on init avoids that flash.
const cacheKey = (orgId) => `kb_reviewer:${orgId}`;

const readCache = (orgId) => {
  if (!orgId) return false;
  try {
    return localStorage.getItem(cacheKey(orgId)) === "1";
  } catch {
    return false;
  }
};

const writeCache = (orgId, value) => {
  if (!orgId) return;
  try {
    localStorage.setItem(cacheKey(orgId), value ? "1" : "0");
  } catch {
    // ignore quota / private-mode errors
  }
};

// Returns true when the current user is a "reviewer" in the active org
// (selected_org_id). Used to gate KB mutating controls on the frontend.
// Fails open (treats as non-reviewer) on any error, so a transient failure
// never wrongly locks out a legitimate user. Initial value comes from the
// per-org cache (above) so it's correct immediately on re-mount.
export default function useIsKbReviewer() {
  const [isReviewer, setIsReviewer] = useState(() =>
    readCache(Cookies.get("selected_org_id"))
  );

  useEffect(() => {
    let active = true;

    const check = async () => {
      try {
        const orgId = Cookies.get("selected_org_id");
        const email = (localStorage.getItem("email") || "").toLowerCase();
        if (!orgId || !email) {
          if (active) setIsReviewer(false);
          return;
        }
        // Reflect the cached verdict for THIS org right away (e.g. after a
        // workspace switch) so we don't flash the previous org's state.
        if (active) setIsReviewer(readCache(orgId));
        const res = await api.get(
          `/api/v1/org/members/list/?organization_id=${orgId}`
        );
        const members = Array.isArray(res.data) ? res.data : [];
        const me = members.find(
          (m) => (m.email || "").toLowerCase() === email
        );
        const reviewer = me?.permission_role === "Reviewer";
        writeCache(orgId, reviewer);
        if (active) setIsReviewer(reviewer);
      } catch {
        if (active) setIsReviewer(false); // fail open
      }
    };

    check();
    const onWorkspaceChange = () => check();
    window.addEventListener("workspace-changed", onWorkspaceChange);
    return () => {
      active = false;
      window.removeEventListener("workspace-changed", onWorkspaceChange);
    };
  }, []);

  return isReviewer;
}
